import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { companiesClient } from '@/lib/api'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { Check, ChevronsUpDown } from 'lucide-react'

export const formatTimestamp = (value: string) =>
  new Date(value).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  })

export interface CompanyOption {
  id: string
  name: string
}

/** Companies for the filter dropdown + id → name resolution */
export function useCompanyNames() {
  const [companies, setCompanies] = useState<CompanyOption[]>([])

  useEffect(() => {
    companiesClient
      .searchCompanies({})
      .then(response =>
        setCompanies(
          (response.companies ?? []).map(company => ({
            id: company.id,
            name: company.name,
          })),
        ),
      )
      .catch(() => setCompanies([]))
  }, [])

  const companyName = (companyId: string) =>
    companies.find(company => company.id === companyId)?.name ?? companyId

  return { companies, companyName }
}

/** Link-based underline tabs for sibling pages (e.g. Triggers ↔ Events); `actions` renders right-aligned on the tab row */
export function SectionTabs({
  tabs,
  actions,
}: {
  tabs: { label: string; to: string; active: boolean }[]
  actions?: React.ReactNode
}) {
  return (
    <div className="flex items-end justify-between gap-4 border-b">
      <div className="flex gap-6">
        {tabs.map(tab => (
          <Link
            key={tab.to}
            to={tab.to}
            className={cn(
              '-mb-px border-b-2 px-0.5 pb-2.5 pt-1 text-sm',
              tab.active
                ? 'border-b-foreground font-medium'
                : 'border-b-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {tab.label}
          </Link>
        ))}
      </div>
      {actions && (
        <div className="flex flex-wrap items-center gap-2 pb-2">{actions}</div>
      )}
    </div>
  )
}

/** Hookdeck-style underline tabs: hides the animated pill, keeps a bottom rule */
export const UNDERLINE_TABS_LIST =
  'h-auto w-full justify-start gap-6 rounded-none border-b bg-transparent p-0 [&>div]:hidden'
export const UNDERLINE_TABS_TRIGGER =
  'rounded-none px-0.5 pb-2.5 pt-1 text-sm border-0 border-b-2 border-b-transparent data-[state=active]:border-b-foreground'

export const PLATFORM_PAGE_SIZE = '20'

/** Sentinel for "no filter" — union it with the real values via FilterValue */
export const FILTER_ALL = 'ALL' as const
export type FilterValue<Value extends string> = Value | typeof FILTER_ALL

export const DATE_RANGES = [
  { value: FILTER_ALL, label: 'All time', minutes: null },
  { value: '1H', label: 'Last hour', minutes: 60 },
  { value: '24H', label: 'Last 24 hours', minutes: 60 * 24 },
  { value: '3D', label: 'Last 3 days', minutes: 60 * 24 * 3 },
  { value: '7D', label: 'Last 7 days', minutes: 60 * 24 * 7 },
  { value: '30D', label: 'Last 30 days', minutes: 60 * 24 * 30 },
] as const

export type DateRangeValue = (typeof DATE_RANGES)[number]['value']

export function sinceForRange(range: DateRangeValue): string | undefined {
  const minutes = DATE_RANGES.find(option => option.value === range)?.minutes
  if (!minutes) return undefined
  return new Date(Date.now() - minutes * 60_000).toISOString()
}

export function CompanyFilter({
  companies,
  value,
  onChange,
}: {
  companies: CompanyOption[]
  value: string
  onChange: (companyId: string) => void
}) {
  const [open, setOpen] = useState(false)
  const selectedName =
    value === FILTER_ALL
      ? 'All companies'
      : (companies.find(company => company.id === value)?.name ?? value)

  const selectCompany = (companyId: string) => {
    onChange(companyId)
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="h-9 w-[210px] justify-between font-normal"
        >
          <span className="truncate">{selectedName}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search companies…" />
          <CommandList className="max-h-[280px]">
            <CommandEmpty>No company found.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="All companies"
                onSelect={() => selectCompany(FILTER_ALL)}
              >
                <Check
                  className={cn(
                    'mr-2 h-4 w-4',
                    value === FILTER_ALL ? 'opacity-100' : 'opacity-0',
                  )}
                />
                All companies
              </CommandItem>
              {companies.map(company => (
                <CommandItem
                  key={company.id}
                  value={company.name}
                  onSelect={() => selectCompany(company.id)}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === company.id ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <span className="truncate">{company.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export function DetailField({
  label,
  children,
  chip = true,
}: {
  label: string
  children: React.ReactNode
  chip?: boolean
}) {
  const isEmpty =
    children === '—' || children === null || children === undefined
  return (
    <div className="flex items-center justify-between gap-4 py-3">
      <span className="shrink-0 text-sm text-foreground/80">{label}</span>
      {chip && !isEmpty ? (
        <span className="min-w-0 break-all rounded border bg-muted px-1.5 py-0.5 text-right font-mono text-[11px]">
          {children}
        </span>
      ) : (
        <span className="min-w-0 break-all text-right font-mono text-xs text-muted-foreground">
          {isEmpty ? '–' : children}
        </span>
      )}
    </div>
  )
}
